<div class="max-h-[calc(100dvh_-_14.5rem)] overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-100">

  <!-- Column header (desktop) -->
  @if(projects.length > 0) {
  <div
    class="hidden md:flex md:items-center md:gap-4 px-4 py-2 bg-gray-50 rounded-t-xl sticky top-0 z-20 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
    <span class="flex-[2.5] min-w-0 pl-5">Project</span>
    <span class="flex-[1.3] min-w-0">Client</span>
    <span class="flex-[1.8] min-w-0">Progress</span>
    <span class="w-24 shrink-0">Due</span>
    <span class="w-28 shrink-0">Team</span>
    <span class="w-24 shrink-0">Status</span>
    <span class="w-6 shrink-0"></span>
  </div>
  }

  @for(project of projects; track project.projectId) {
  @let done = isProjectDone(project);
  @let sm = statusPill(project);
  @let dl = getDeadline(project);
  @let encoded = getEncodedHours(project.projectId);
  @let pct = getBudgetPercentage(project);
  @let team = getFusedTeam(project);
  @let dot = done ? '#9ca3af' : dotColor(project.projectId || project.projectName);
  <div (click)="onProjectClick(project.projectId)" (keydown.enter)="onProjectClick(project.projectId)"
    (keydown.space)="onProjectClick(project.projectId); $event.preventDefault()" tabindex="0" role="button"
    class="project-row group relative bg-white cursor-pointer
           flex flex-col gap-2.5 px-4 py-3
           md:flex-row md:items-center md:gap-4
           first:rounded-t-xl last:rounded-b-xl transition-colors hover:bg-gray-50
           focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/40"
    [class.opacity-70]="done">

    <!-- Project: dot + name + hours -->
    <div class="flex items-center gap-3 min-w-0 md:flex-[2.5]">
      <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" [style.background]="dot"></span>
      <div class="flex flex-col min-w-0">
        <h2 class="text-sm font-semibold text-gray-800 truncate group-hover:text-blue-600 transition-colors"
          title="{{ project.projectName }}">
          {{ project.projectName }}
        </h2>
        <span class="text-xs text-gray-400 tabular-nums">
          @if (project.estimatedHours && project.estimatedHours > 0) {
          {{ encoded | number:'1.0-0' }}h / {{ project.estimatedHours }}h
          } @else if (encoded > 0) {
          {{ encoded | number:'1.0-0' }}h
          } @else { — }
        </span>
      </div>
    </div>

    <!-- Client -->
    <div class="flex items-center min-w-0 md:flex-[1.3]">
      <span class="md:hidden text-[11px] font-semibold text-gray-400 uppercase tracking-wider mr-2">Client</span>
      @if (project.client) {
      <span class="text-sm text-gray-600 truncate" title="{{ project.client.name }}">{{ project.client.name }}</span>
      } @else {
      <span class="text-sm text-gray-300">—</span>
      }
    </div>

    <!-- Progress -->
    <div class="flex items-center gap-2 min-w-0 md:flex-[1.8]">
      <span class="md:hidden text-[11px] font-semibold text-gray-400 uppercase tracking-wider mr-2">Progress</span>
      @if (project.estimatedHours && project.estimatedHours > 0) {
      <div class="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden max-w-[10rem]">
        <div class="h-full rounded-full transition-all" [style.width.%]="pct" [style.background]="dot"></div>
      </div>
      <span class="text-sm font-semibold text-gray-700 tabular-nums w-9 text-right">{{ pct | number:'1.0-0' }}%</span>
      } @else {
      <span class="text-sm text-gray-300">—</span>
      }
    </div>

    <!-- Due -->
    <div class="flex items-center md:w-24 md:shrink-0">
      <span class="md:hidden text-[11px] font-semibold text-gray-400 uppercase tracking-wider mr-2">Due</span>
      @if (dl) {
      <span class="text-sm tabular-nums" [ngClass]="deadlineClass(project)">{{ dl | date:'d MMM' }}</span>
      } @else {
      <span class="text-sm text-gray-300">—</span>
      }
    </div>

    <!-- Team — click the avatars to assign members (admins) -->
    @let canAssignTeam = userAuthService.admin();
    <div class="flex items-center md:w-28 md:shrink-0">
      <span class="md:hidden text-[11px] font-semibold text-gray-400 uppercase tracking-wider mr-2">Team</span>
      @if (team.length > 0) {
      <div class="flex items-center rounded-full transition-all"
        [class.cursor-pointer]="canAssignTeam"
        [class.hover:bg-blue-50]="canAssignTeam"
        [class.px-1]="canAssignTeam"
        [class.-mx-1]="canAssignTeam"
        [attr.role]="canAssignTeam ? 'button' : null"
        [attr.tabindex]="canAssignTeam ? 0 : null"
        [title]="canAssignTeam ? 'Assign team members' : ''"
        (click)="onTeamClick(project, $event)"
        (keydown.enter)="onTeamClick(project, $event)">
        <div class="flex -space-x-2">
          @for (person of team; track person.id; let i = $index) {
          @if (i < 3) {
          <span
            class="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-white text-[10px] font-bold"
            [class.bg-green-100]="person.role === 'manager'" [class.text-green-700]="person.role === 'manager'"
            [class.bg-blue-100]="person.role === 'member'" [class.text-blue-700]="person.role === 'member'"
            [class.bg-gray-100]="person.role === 'assignee'" [class.text-gray-600]="person.role === 'assignee'"
            [title]="(person.role === 'manager' ? 'Manager' : person.role === 'member' ? 'Member' : 'Task assignee') + ': ' + person.firstName + ' ' + person.lastName">
            {{ initials(person.firstName, person.lastName) }}
          </span>
          }
          }
        </div>
        @if (team.length > 3) {
        <span class="ml-1.5 text-xs text-gray-400 font-semibold">+{{ team.length - 3 }}</span>
        }
      </div>
      } @else if (canAssignTeam) {
      <button (click)="openManageMembers(project.projectId, project.projectName); $event.stopPropagation()"
        title="Assign team members"
        class="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-gray-300 text-gray-400
               hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
      </button>
      } @else {
      <span class="text-sm text-gray-300">—</span>
      }
    </div>

    <!-- Status -->
    <div class="flex items-center md:w-24 md:shrink-0">
      <span class="md:hidden text-[11px] font-semibold text-gray-400 uppercase tracking-wider mr-2">Status</span>
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border" [ngClass]="sm.cls">
        {{ sm.label }}
      </span>
    </div>

    <!-- Admin overflow menu (hover) -->
    <div class="absolute top-2 right-2 md:static md:w-6 md:shrink-0 md:flex md:justify-end">
      @if(userAuthService.admin()) {
      <div class="relative">
        <button (click)="toggleMenu(project.projectId!); $event.stopPropagation()"
          [class.opacity-100]="openMenuId === project.projectId"
          class="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition
                 md:opacity-0 md:group-hover:opacity-100 md:group-focus-visible:opacity-100">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="5" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
          </svg>
        </button>
        @if(openMenuId === project.projectId) {
        <div class="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
          <button (click)="openManageManagers(project.projectId, project.projectName); $event.stopPropagation()"
            class="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2 cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Manage Managers
          </button>
          <button (click)="openManageMembers(project.projectId, project.projectName); $event.stopPropagation()"
            class="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Manage Team Members
          </button>
          <div class="my-1 border-t border-gray-100"></div>
          <div class="px-4 pt-1 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">Status</div>
          @for(choice of statusChoices; track choice.value) {
          <button (click)="setStatus(project, choice.value, $event)"
            [disabled]="isCurrentStatus(project, choice.value)"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between gap-2 cursor-pointer disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-default">
            {{ choice.label }}
            @if(isCurrentStatus(project, choice.value)) {
            <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            }
          </button>
          }
          <div class="my-1 border-t border-gray-100"></div>
          <button (click)="deleteProject(project.projectId); $event.stopPropagation()"
            class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Project
          </button>
        </div>
        }
      </div>
      }
    </div>
  </div>
  }@empty {
  <div class="py-16 text-center bg-white rounded-xl">
    <svg class="w-16 h-16 text-gray-200 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
    <p class="text-gray-500 font-medium mb-1">No projects found</p>
    <p class="text-sm text-gray-400">Create your first project to get started</p>
  </div>
  }
</div>
@if(showConfirm) {
<app-confirm-modal [message]="confirmMessage" (close)="handleConfirm($event)"></app-confirm-modal>
}

@if(showManageManagers && selectedProjectId) {
<app-manage-project-managers [projectId]="selectedProjectId" [projectName]="selectedProjectName || ''"
  (close)="closeManageManagers()">
</app-manage-project-managers>
}

@if(showManageMembers && selectedProjectId) {
<app-manage-project-members [projectId]="selectedProjectId" [projectName]="selectedProjectName || ''"
  (close)="closeManageMembers()">
</app-manage-project-members>
}
